/**
 * Krok UX1 — SUROVÉ INTERNÍ TEXTY MAKLÉŘI NIKDY NEVIDÍ.
 *
 * Nález AUD1 28. 9. 2026 (bod 4, od CZ1): ve starším běhu z 1. 9. se na
 * obrazovce objevilo
 *   „note: Soucet KN vymer … (VERIFIED ČÚZK/RÚIAN, 2026-09-01) … · deltaM2: 303“
 * a jinde strojové identifikátory (`GATE_OWNERSHIP`, `01a05edd-…`).
 * Kód po CZ1 texty už skládá česky, ale **persistovaná data** zůstala a
 * fallbacky `CAPTIONS[key] ?? key` ve větvičce kladou klíč na obrazovku,
 * když popisek neznáme.
 *
 * Pravidlo tady: text určený člověku buť projde kontrolou `checkHumanText`
 * (krok CZ1 — interní kód, anglicismus, překlep, diakritika), nebo se
 * neukáže vůbec. Místo něj dostane makléř „Text se připravuje“ — což je
 * pravda a nevyzradí, že za tím je rozpracovaný běh.
 *
 * ROZHODNUTÍ: filtr je ve `web-admin` (display vrstva), ne v generátorech
 * reportu — `packages/reports` a `packages/contexts/duediligence` jsou
 * zlatý standard tohoto kroku. Překlep text NEMAŽE (je to pořád česky
 * čitelná věta); skrývá se to, co je strojové.
 *
 * ---------------------------------------------------------------------------
 * ZMĚNA FUNK1 bod 6 (R.B. 4. 10. 2026 04:45) — TEXT BEZ DIAKRITIKY TAKY NE.
 *
 * Do FUNK1 chybějící diakritika text nezastavila. Průchod celou aplikací ale
 * našel na detailu zakázky věty jako „Rozdil 30 m2 (1.4 procenta) muze byt
 * jen prepis…" a „Rozhoduje, zda lze rovnou zadat uzemni/stavebni rizeni…" —
 * surový výstup staršího běhu průzkumu, který leží v datech. Zadání je
 * jednoznačné: **„Makléř nesmí vidět text bez háčků a čárek."**
 *
 * Opravit to „u zdroje" znamená dvě věci a obě tenhle krok dělá:
 *  1. slovník `AUDIT_DIACRITICS_RULES` (@reos/i18n) zná slova z nálezu, takže
 *     nový text takhle nevznikne (kontrola `checkHumanText` běží nad tím, co
 *     vzniká), a `pnpm cestina:audit` je najde i v DB,
 *  2. text, který už v datech JE, se makléři neukáže — místo něj stojí věta
 *     z katalogu (fallback volajícího). Přepsat ho za autora nejde: hádat
 *     háčky ve větě, kterou jsme nenapsali, je výmysl, ne oprava.
 * ---------------------------------------------------------------------------
 */

import { checkHumanText, t } from '@reos/i18n';

/** Co makléř uvidí místo surového interního textu. */
export const TEXT_PREPARING = t('webAdmin.brokerText.preparing', { locale: 'cs' });

/** Kódy, po nichž text není pro makléře (viz hlavička, ZMĚNA FUNK1 bod 6). */
const MACHINE_TEXT_RULES: readonly HumanTextCode[] = [
  'INTERNAL_TOKEN',
  'ENGLISH_WORD',
  'MISSING_DIACRITICS',
];

type HumanTextCode = 'INTERNAL_TOKEN' | 'ENGLISH_WORD' | 'MISSING_DIACRITICS';

/** UUID, long hex a objectid — katalogové ID, fakt ID, tenant ID. */
const IDENTIFIER_PATTERNS: readonly RegExp[] = [
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/iu,
  /\b[0-9a-f]{24}\b/iu,
  /\b[0-9a-f]{16,}\b/iu,
];

/** `note:`, `deltaM2:`, `reason_code:` — název pole na začátku věty. */
const FIELD_LABEL_PATTERN = /(^|[\s(;–—-])[a-z][A-Za-z0-9_]*\s*:/u;

/**
 * Klíč katalogu s pomlčkou: `Q-PLAN`, `Q-ACCESS-OBSTACLE-1`, `GATE-OWNERSHIP`.
 *
 * Krok UX2, živý běh 30. 9. 2026 nad Poděbradova 141/44: na kokpitu makléře
 * stálo „Primární cíl klienta; závisí na Q-PLAN/Q-ACCESS/Q-UTIL.“
 * `checkHumanText` (CZ1) hlídá podtržítko a camelCase, tenhle tvar ne.
 *
 * Tvar je schválně úzký — VELKÁ PÍSMENA, pomlčka, na obou stranách aspoň dvě
 * písmena bez diakritiky. České zkratky („LV“, „ÚP“, „ČÚZK“, „SVJ“) pomlčku
 * nemají a „e-mail“ začíná malým písmenem, takže je to nechá projít.
 */
const CATALOG_KEY_PATTERN = /\b[A-Z][A-Z0-9]*-[A-Z][A-Z0-9]*(?:-[A-Z0-9]+)*\b/u;

/**
 * Zbytek strojového enumu v jinak lidské větě — v závorce na konci:
 * „…cenová očekávání (PAST/CHANGE).“, „…zastavitelnost i hodnotu (FUTURE režim).“
 *
 * Krok UX2, živý běh 30. 9. 2026 nad Poděbradova 141/44: obě věty prošly
 * `isMachineText` (nemají pomlčku ani camelCase), takže se makléři ukázaly
 * s kódem uvnitř. Celou větu zahodit nelze — je jinak v pořádku a je to
 * jediná věta, kterou u té položky máme.
 *
 * Tvar je schválně úzký a míří jen na dvě podoby úniku:
 *   1. VELKÁ PÍSMENA s lomítkem — `PAST/CHANGE`, `A/B`,
 *   2. VELKÁ PÍSMENA (4+) plus jedno české slovo — `FUTURE režim`.
 * Samostatná česká zkratka v závorce („(LV)“, „(PVK)“, „(RUIAN)“, „(ČÚZK)“)
 * ani jednomu tvaru nesedí a zůstává — nese pro makléře význam.
 *
 * Proč 4+ a ne 3+: v datech MV reality jsou v závorce i české regulativy
 * („(NZ zemedelska)“, „(RP Hrabanov jiz plati)“), které význam nesou a zmizet
 * nesmí. Dvě písmena by je shodila, čtyři drží rezervu i pro „SVJ“ a spol.
 * `[A-Z]` je v `u` režimu jen ASCII, takže „ČÚZK“ se sem nikdy nedostane.
 */
const ENUM_LEAK_PATTERN =
  /\s*\((?:[A-Z][A-Z0-9]*(?:\/[A-Z0-9]+)+|[A-Z]{4,}\s+[a-záčďéěíňóřšťúůýž]+)\)/gu;

/** Odřízne strojový enum v závorce; zbytek věty nechá být. */
function stripEnumLeak(text: string): string {
  return text.replace(ENUM_LEAK_PATTERN, '');
}

/**
 * Odkaz není název pole — `https:` by vypadal jako `note:`. Maskuje se
 * stejně jako v `human-text.ts`, jen abychom z něj nevytáhli kód.
 */
const URL_PATTERN = /(?:https?:\/\/|www\.)\S+/giu;

function maskLinks(text: string): string {
  return text.replace(URL_PATTERN, (match) => ' '.repeat(match.length));
}

/** Je to strojový text, který nesmí do UI pro člověka? */
export function isMachineText(raw: string): boolean {
  if (raw.trim() === '') return true;
  if (IDENTIFIER_PATTERNS.some((pattern) => pattern.test(raw))) return true;
  if (CATALOG_KEY_PATTERN.test(maskLinks(raw))) return true;
  if (FIELD_LABEL_PATTERN.test(maskLinks(raw))) return true;
  return checkHumanText(raw).some((issue) => (MACHINE_TEXT_RULES as readonly string[]).includes(issue.code));
}

export interface BrokerTextOptions {
  /** Co ukázat, když text chybí nebo je strojový. Výchozí = „Text se připravuje“. */
  readonly fallback?: string;
}

/**
 * Text pro makléře. Čistá věta projde beze změny; interní kód, anglicismus
 * nebo strojový identifikátor nahradí `fallback` (default „Text se
 * připravuje“), aby na obrazovce nebyla ani mezera.
 */
export function brokerText(raw: string | null | undefined, options: BrokerTextOptions = {}): string {
  const fallback = options.fallback ?? TEXT_PREPARING;
  if (raw === null || raw === undefined) return fallback;
  // Nejdriv odriznout enum v zavorce, teprve pak rozhodovat o strojovosti —
  // veta „…ocekavani (PAST/CHANGE)." je po odrizuti v poradku a ma se ukazat.
  const cleaned = stripEnumLeak(raw);
  return isMachineText(cleaned) ? fallback : cleaned;
}

/**
 * Popisek z katalogu pro kód (brána, důkaz, role, stav). Když popisek
 * neznáme, neukážeme samotný klíč — ten je pro stroj, ne pro makléře.
 * Klíč (`_key`) je v podpisu schválně: u(call site) je vidět, že o
 * fallbacku rozhoduje tento filtr, ne náhoda.
 */
export function captionOrPreparing(caption: string | undefined, _key: string): string {
  if (caption === undefined || caption.trim() === '') return TEXT_PREPARING;
  return caption;
}
